import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import useMediaQuery from "@core-ui/hooks/useMediaQuery";
import useWatch from "@core-ui/hooks/useWatch";
import { cssMedia } from "@core-ui/utils/cssUtils";
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import { HIGHLIGHT_COLOR_NAMES } from "@ext/markdown/elements/highlight/edit/model/consts";
import EditorService from "@ext/markdown/elementsUtils/ContextServices/EditorService";
import type { Editor } from "@tiptap/core";
import { ColorTile } from "@ui-kit/ColorTile";
import { Popover, PopoverContent, PopoverTrigger } from "@ui-kit/Popover";
import { ComponentVariantProvider } from "@ui-kit/Providers";
import { ToolbarIcon, ToolbarToggleButton, ToolbarTriggerChevron } from "@ui-kit/Toolbar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { type MouseEvent, memo, useCallback, useRef, useState } from "react";

const HighlightButton = styled(ColorTile)<{ color: HIGHLIGHT_COLOR_NAMES }>`
	background-color: ${({ color }) => `var(--color-highlight-${color})`};
`;

const StyledButton = styled(ToolbarToggleButton)`
	.fill-current {
		fill: var(--color-highlight-${({ color }) => color});
	}
`;

const HighlightMenuButton = ({ editor }: { editor: Editor }) => {
	const { isActive: active, disabled, attrs } = ButtonStateService.useCurrentAction({ mark: "highlight" });
	const isMobile = useMediaQuery(cssMedia.JSnarrow);
	const [activeColor, setActiveColor] = useState<HIGHLIGHT_COLOR_NAMES>(editor.getAttributes("highlight")?.color);
	const lastUsedColor = EditorService.getData("lastUsedHighlightColor") ?? HIGHLIGHT_COLOR_NAMES.PEONY;
	const portalContainerRef = useRef<HTMLDivElement>(null);

	useWatch(() => {
		setActiveColor(attrs?.color ? (attrs.color as HIGHLIGHT_COLOR_NAMES) : HIGHLIGHT_COLOR_NAMES.DEFAULT);
	}, [attrs?.color]);

	const isActive = active && activeColor !== HIGHLIGHT_COLOR_NAMES.DEFAULT;

	const onClickHandler = useCallback(
		(event: MouseEvent<HTMLDivElement>, color: HIGHLIGHT_COLOR_NAMES) => {
			event.preventDefault();
			if (color === HIGHLIGHT_COLOR_NAMES.DEFAULT) {
				editor.commands.unsetHighlight();
				setActiveColor(HIGHLIGHT_COLOR_NAMES.DEFAULT);
				return;
			}

			EditorService.setData("lastUsedHighlightColor", color);
			editor.commands.setHighlight({ color });
			setActiveColor(color);
		},
		[editor],
	);

	const onTriggerClick = useCallback(() => {
		if (isActive) {
			editor.commands.unsetHighlight();
			setActiveColor(HIGHLIGHT_COLOR_NAMES.DEFAULT);
			return;
		}

		const color = lastUsedColor ?? HIGHLIGHT_COLOR_NAMES.PEONY;
		editor.commands.setHighlight({ color });
		setActiveColor(color);
	}, [isActive, editor, lastUsedColor]);

	const isActiveColor = useCallback(
		(name: HIGHLIGHT_COLOR_NAMES) => {
			if (name === HIGHLIGHT_COLOR_NAMES.DEFAULT && !activeColor) return true;
			return activeColor === name;
		},
		[activeColor],
	);

	const onAutoCloseFocus = useCallback(
		(event: Event) => {
			event.preventDefault();
			editor.commands.focus();
		},
		[editor],
	);

	return (
		<ComponentVariantProvider variant="inverse">
			<StyledButton
				active={isActive}
				className="text-inverse-primary-fg"
				color={lastUsedColor}
				disabled={disabled}
				onClick={onTriggerClick}
			>
				<ToolbarIcon icon="color-highlighter" />
			</StyledButton>
			<Popover>
				<PopoverTrigger asChild>
					<ToolbarTriggerChevron disabled={disabled} focusable sub />
				</PopoverTrigger>
				<PopoverContent
					alignOffset={!isMobile ? -18 : -5}
					className="bg-transparent px-3 py-3 pb-2 border-none w-auto rounded-xl"
					onCloseAutoFocus={onAutoCloseFocus}
					portalContainer={portalContainerRef.current}
					side="top"
					sideOffset={0}
					style={{ boxShadow: "none" }}
				>
					<div className="flex items-center p-1 gap-1 w-auto bg-inverse-primary-bg rounded-lg lg:shadow-hard-base overflow-hidden">
						{Object.values(HIGHLIGHT_COLOR_NAMES).map((color) => (
							<Tooltip key={color}>
								<TooltipTrigger asChild>
									<HighlightButton
										color={color}
										onClick={(event) => onClickHandler(event, color)}
										selected={isActiveColor(color)}
									/>
								</TooltipTrigger>
								<TooltipContent>{t(`editor.highlight.colors.${color}`)}</TooltipContent>
							</Tooltip>
						))}
					</div>
				</PopoverContent>
			</Popover>
		</ComponentVariantProvider>
	);
};

export default memo(HighlightMenuButton);
